import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataTable,
  DataTableRowReorderEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { AppDialog, RowActions, StatusBadge, emptyMessage, useTenantGate, rowActionsTableStyle } from "../ui";
import { Avatar } from "primereact/avatar";
import toast from "react-hot-toast";

import {
  useGetBenefitPartnersQuery,
  useDeleteBenefitPartnerMutation,
  useGetBenefitCategoriesQuery,
  useReorderBenefitPartnersMutation,
} from "../../store/services/benefits";
import { useDebounce } from "../../hooks/useDebounce";
import {
  BenefitPartner,
  BenefitPartnersParams,
  BenefitStatus,
} from "../../models/benefits";
import {
  getBenefitStatusLabel,
  getBenefitStatusTone,
} from "../../utils/constants/benefits-constants";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { BenefitPartnerFilters } from "../Filters/BenefitPartnerFilters";

export const BenefitPartnerListDT = () => {
  const navigate = useNavigate();

  // Filtros: búsqueda de texto client-side; el resto va a la query.
  const [search, setSearch] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [status, setStatus] = useState<BenefitStatus | undefined>();
  const [featured, setFeatured] = useState<boolean | undefined>();

  const clearAllFilters = () => {
    setSearch("");
    setCategoryId(undefined);
    setStatus(undefined);
    setFeatured(undefined);
  };

  const debouncedSearch = useDebounce(search, 500);
  const effectiveSearch = search === "" ? "" : debouncedSearch;

  const queryParams = useMemo<BenefitPartnersParams>(
    () => ({ categoryId, status, featured }),
    [categoryId, status, featured]
  );

  // El listado no fuerza tenant: en scope global muestra los partners de todos
  // los tenants, que es justamente lo útil de esta pantalla.
  const { data: partners, isLoading } = useGetBenefitPartnersQuery(queryParams);
  const { data: categories } = useGetBenefitCategoriesQuery({ tenantId: null });
  const [deletePartner, { isLoading: isDeleting }] =
    useDeleteBenefitPartnerMutation();

  const [partnerToDelete, setPartnerToDelete] = useState<BenefitPartner | undefined>();

  // Orden por arrastre (como Categorías y los destacados). El orden es por
  // tenant y se guarda con la lista completa, así que solo se habilita con un
  // tenant elegido y sin filtros: con filtros o páginas faltarían partners.
  const tenantGate = useTenantGate();
  const [reorderPartners, { isLoading: isSavingOrder }] =
    useReorderBenefitPartnersMutation();
  const [orderedRows, setOrderedRows] = useState<BenefitPartner[] | null>(null);
  const hasFilters = Boolean(
    search.trim() || categoryId || status || featured !== undefined
  );
  const canReorder = tenantGate.allowed && !hasFilters;

  // Cuando llega la lista del servidor (ya ordenada por sort_order) se descarta
  // el orden optimista local.
  useEffect(() => {
    setOrderedRows(null);
  }, [partners]);

  const handleRowReorder = (e: DataTableRowReorderEvent<BenefitPartner[]>) => {
    const reordered = e.value;
    setOrderedRows(reordered);
    reorderPartners(reordered.map((partner) => partner.id))
      .unwrap()
      .then(() => toast.success("Orden actualizado"))
      .catch((err) => {
        setOrderedRows(null);
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo actualizar el orden"));
      });
  };

  // Orden por columna controlado: sin control, la tabla se queda con el orden por
  // nombre de la primera carga aunque se pase al modo de orden manual.
  const [columnSort, setColumnSort] = useState<{
    field?: string;
    order?: DataTableSortEvent["sortOrder"];
  }>({ field: "name", order: 1 });

  const reorderHint = canReorder
    ? "Arrastrá las filas para definir en qué orden aparecen los beneficios de cada partner en la app."
    : tenantGate.allowed
      ? "Para cambiar el orden, limpiá los filtros."
      : "Para cambiar el orden, elegí un tenant arriba.";

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    (categories ?? []).forEach((category) => {
      map.set(category.id, category.name || category.id);
    });
    return map;
  }, [categories]);

  const filteredPartners = useMemo(() => {
    const term = effectiveSearch.trim().toLowerCase();
    const list = partners ?? [];
    if (!term) return list;
    return list.filter(
      (partner) =>
        (partner.name ?? "").toLowerCase().includes(term) ||
        (partner.slug ?? "").toLowerCase().includes(term)
    );
  }, [partners, effectiveSearch]);

  const hideDeleteDialog = () => setPartnerToDelete(undefined);

  const handleDeletePartner = (partner: BenefitPartner) => {
    // El partner puede ser de otro tenant que el del selector superior: se
    // borra siempre en el suyo.
    deletePartner({ id: partner.id, tenantId: partner.tenantId })
      .unwrap()
      .then(() => toast.success("Partner eliminado"))
      .catch((err) =>
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo eliminar el partner")
        )
      )
      .finally(() => hideDeleteDialog());
  };

  // Column templates
  const logoBodyTemplate = (partner: BenefitPartner) =>
    partner.logoUrl ? (
      <Avatar
        image={partner.logoUrl}
        shape="circle"
        style={{ width: "40px", height: "40px" }}
        imageAlt={partner.name ?? "Logo"}
      />
    ) : (
      <Avatar
        icon="pi pi-image"
        shape="circle"
        style={{ width: "40px", height: "40px" }}
      />
    );

  const nameBodyTemplate = (partner: BenefitPartner) => (
    <div className="flex flex-column">
      <span>{partner.name}</span>
      {partner.slug && (
        <small className="text-color-secondary">{partner.slug}</small>
      )}
    </div>
  );

  const categoryBodyTemplate = (partner: BenefitPartner) =>
    partner.categoryId ? categoryNameById.get(partner.categoryId) ?? "-" : "-";

  const featuredBodyTemplate = (partner: BenefitPartner) =>
    partner.featured ? (
      <i
        className="pi pi-star-fill text-yellow-500"
        title="Destacado"
        aria-label="Destacado"
      />
    ) : null;

  const statusBodyTemplate = (partner: BenefitPartner) => (
    <StatusBadge
      label={getBenefitStatusLabel(partner.status)}
      tone={getBenefitStatusTone(partner.status)}
    />
  );

  // 3 acciones: RowActions las deja como iconos en linea (INC-03).
  const actionsBodyTemplate = (partner: BenefitPartner) => (
    <RowActions
      actions={[
        { icon: "pi pi-eye", label: "Ver", severity: "info", onClick: () => navigate(`${partner.id}`) },
        { icon: "pi pi-pencil", label: "Editar", onClick: () => navigate(`${partner.id}/edit`) },
        { icon: "pi pi-trash", label: "Eliminar", severity: "danger", onClick: () => setPartnerToDelete(partner) },
      ]}
    />
  );

  return (
    <>
      <DataTable
        style={rowActionsTableStyle(canReorder ? "4.5rem" : "1rem", "10rem")}
        value={canReorder ? orderedRows ?? filteredPartners : filteredPartners}
        loading={isLoading || isDeleting || isSavingOrder}
        // En modo orden se muestran todos juntos: arrastrar entre páginas no se puede.
        paginator={!canReorder}
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage={emptyMessage("partners")}
        header={
          <>
            <BenefitPartnerFilters
              search={search}
              setSearch={setSearch}
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              status={status}
              setStatus={setStatus}
              featured={featured}
              setFeatured={setFeatured}
              onClearAll={clearAllFilters}
            />
            <p className="m-0 mt-3 text-sm text-600 font-normal">{reorderHint}</p>
          </>
        }
        stripedRows
        // En modo orden la tabla muestra el orden real (sort_order), no por nombre.
        sortField={canReorder ? undefined : columnSort.field}
        sortOrder={canReorder ? undefined : columnSort.order}
        onSort={(e) => setColumnSort({ field: e.sortField, order: e.sortOrder })}
        removableSort
        reorderableRows={canReorder && !isSavingOrder}
        onRowReorder={handleRowReorder}
        dataKey="id"
      >
        {canReorder && <Column rowReorder style={{ width: "3.5rem", minWidth: "3.5rem" }} />}
        <Column
          header="Logo"
          body={logoBodyTemplate}
          style={{ width: "5rem" }}
          exportable={false}
        />
        <Column
          field="name"
          header="Nombre"
          body={nameBodyTemplate}
          sortable={!canReorder}
          style={{ minWidth: "14rem" }}
        />
        <Column
          header="Categoría"
          body={categoryBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="featured"
          header="Destacado"
          body={featuredBodyTemplate}
          sortable={!canReorder}
          style={{ minWidth: "7rem" }}
        />
        <Column
          field="status"
          header="Estado"
          body={statusBodyTemplate}
          sortable={!canReorder}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header={<span className="row-actions-label">Acciones</span>}
          // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
          headerClassName="row-actions-th"
          bodyClassName="row-actions-cell"
          body={actionsBodyTemplate}
          style={{ minWidth: "10rem" }}
          exportable={false}
        />
      </DataTable>

      <AppDialog
        variant="destructive"
        visible={!!partnerToDelete}
        onHide={hideDeleteDialog}
        header="Eliminar partner"
        primaryLabel="Eliminar Partner"
        onPrimary={() => partnerToDelete && handleDeletePartner(partnerToDelete)}
        loading={isDeleting}
      >
        <span>
          ¿Estás seguro de que querés eliminar el partner{" "}
          <strong>{partnerToDelete?.name}</strong>?
          <br />
          <small className="text-color-secondary">
            Al eliminarlo, también se van a ocultar sus beneficios en la app.
          </small>
        </span>
      </AppDialog>
    </>
  );
};
